import React from 'react';
import useStyledTheme from 'shared/hooks/use-styled-theme';
import RadioCheckedIcon from 'shared/assets/radio-checked';
import RadioUncheckedIcon from 'shared/assets/radio-unchecked';
import { TabValue } from 'src/checkout/components/sections/payment/shared/context';
import {
  AlertContainer,
  Badge,
  BadgeContent,
  BadgeLabel,
  RadioContainer,
  Tab,
  TabContent,
  TabLabel,
  TabList,
  TabListSection,
  TabsContainer,
  TabText,
  TabTextGroup,
} from './payment-tabs.styles';
import { PaymentBanner } from '../../shared/banner';

export type TabOption = {
  value: TabValue;
  label: string;
  sublabel?: string;
  badgeLabel: string;
  badgeIcon?: React.ReactNode;
  hasError?: boolean;
};

type PaymentTabsProps = {
  children: React.ReactNode;
  onTabChange: (tab: string) => void;
  selectedTab: TabValue;
  tabOptions: TabOption[];
  showFeeBadgeInline?: boolean;
  showEmbeddedSearch?: boolean;
  alertMessage?: string;
};

const hasTabSelected = (selectedTab: TabValue): boolean => selectedTab !== TabValue.none;

const RadioIndicator = ({ selected, color }: { selected: boolean; color?: string }): JSX.Element => (
  <RadioContainer>{selected ? <RadioCheckedIcon color={color} /> : <RadioUncheckedIcon />}</RadioContainer>
);

const FeeBadge = ({ badgeLabel, badgeIcon }: { badgeLabel: string; badgeIcon?: React.ReactNode }): JSX.Element => (
  <Badge>
    <BadgeContent>
      {!!badgeIcon && badgeIcon}
      <BadgeLabel>{badgeLabel}</BadgeLabel>
    </BadgeContent>
  </Badge>
);

export const PaymentTabs: React.FC<PaymentTabsProps> = ({
  selectedTab,
  onTabChange,
  children,
  tabOptions,
  showFeeBadgeInline = false,
  showEmbeddedSearch = false,
  alertMessage,
}) => {
  const theme = useStyledTheme();
  const accentColor: string = theme?.customized?.colors?.buttonsLinks ?? theme?.colors?.primaryBlue;
  const showNoSelectionError = !!alertMessage && !hasTabSelected(selectedTab);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = tabOptions.findIndex((tab) => tab.value === selectedTab);
    const totalTabs = tabOptions.length;

    const keyMap: Partial<Record<string, number>> = {
      arrowright: (currentIndex + 1) % totalTabs,
      arrowleft: (currentIndex - 1 + totalTabs) % totalTabs,
    };

    const newIndex = keyMap[e.key.toLowerCase()];
    if (newIndex !== undefined && newIndex !== currentIndex) {
      e.preventDefault();
      onTabChange(tabOptions[newIndex].value);
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const focusedTabIndex = tabOptions.findIndex((tab) => document.activeElement?.id === `tab-${tab.value}`);
      if (focusedTabIndex !== -1) {
        onTabChange(tabOptions[focusedTabIndex].value);
      }
    }
  };

  const firstTabValue = tabOptions[0]?.value ?? '';

  return (
    <TabsContainer data-testid='payment-tabs-v2' showEmbeddedSearch={showEmbeddedSearch}>
      <TabListSection>
        {!!alertMessage && (
          <AlertContainer isEmbeddedSearch={showEmbeddedSearch}>
            <PaymentBanner description={alertMessage} severity='errorV2' />
          </AlertContainer>
        )}
        <TabList role='tablist'>
          {tabOptions.map(({ value, label, sublabel, badgeLabel, badgeIcon, hasError: tabHasError }, index) => {
            const isSelected = selectedTab === value;
            const isFirstTab = index === 0;
            const isFocusable = isSelected || (!hasTabSelected(selectedTab) && isFirstTab);

            return (
              <Tab
                key={value}
                role='tab'
                id={`tab-${value}`}
                aria-controls={`tabpanel-${value}`}
                aria-selected={isSelected}
                tabIndex={isFocusable ? 0 : -1}
                selected={isSelected}
                error={showNoSelectionError || tabHasError}
                onClick={() => onTabChange(value)}
                onKeyDown={handleKeyDown}
                accentColor={accentColor}
              >
                <RadioIndicator selected={isSelected} color={accentColor} />
                <TabLabel>
                  <TabTextGroup gap={showFeeBadgeInline ? 2 : 1}>
                    <TabText size='medium' weight={isSelected ? 'semi' : 'regular'} selected={isSelected}>
                      {label}
                    </TabText>
                    {!!sublabel && (
                      <TabText size='medium' selected={isSelected}>
                        {sublabel}
                      </TabText>
                    )}
                    {!!badgeLabel && showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} badgeIcon={badgeIcon} />}
                  </TabTextGroup>
                  {!!badgeLabel && !showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} />}
                </TabLabel>
              </Tab>
            );
          })}
        </TabList>
      </TabListSection>

      {hasTabSelected(selectedTab) && (
        <TabContent
          role='tabpanel'
          id={`tabpanel-${selectedTab}`}
          aria-labelledby={`tab-${selectedTab}`}
          tabIndex={0}
          selectedTab={selectedTab}
          isFirst={selectedTab === firstTabValue}
          error={tabOptions.some((tab) => tab.hasError)}
          showEmbeddedSearch={showEmbeddedSearch}
        >
          {children}
        </TabContent>
      )}
    </TabsContainer>
  );
};
